import React, {useEffect, useState} from "react";
import {useFetch} from "../../hooks/useFetch";
import {useStore} from "../../hooks/useStore";
import {DatePicker, Empty, Table, Tooltip} from "antd";
import locale from "antd/es/date-picker/locale/tr_TR";
import moment from "moment";
import {CustomButton} from "../_defaultComponents/CustomButton/CustomButton";
import {
    FileSearchOutlined,
    SearchOutlined,
    DownloadOutlined,
    InfoCircleOutlined
} from "@ant-design/icons";
import {CityFilter, CompanyFilter} from "../_defaultComponents/FilterComponents";
import {CloseIcon, SearchInput} from "../_defaultComponents/FilterComponents/FilterComponentStyles";
import {b64DecodeToUrl} from "../../utils/helpers";
import {EmptyMessage} from "../_defaultComponents/EmptyMessage/EmptyMessage";

export const CariList = () => {
    const stores = useStore();
    const [subCompanyId, setSelectedSubCompanyId] = useState(undefined);
    const [pageParam, setPageParam] = useState(1);
    const defaultDateRange = [moment().subtract(30, "days"), moment()];
    const [selectedDateRange, setSelectedDateRange] = useState(defaultDateRange);
    const [city, setCity] = useState("all");
    const [loadingExcelData, setLoadingExcelData] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState();
    const [searchText, setSearchText] = useState("");
    const [cariText, setCariText] = useState("");
    const [cariCode, setCariCode] = useState("");
    const [cariCountText, setCariCountText] = useState("");
    const [cariCount, setCariCount] = useState("");
    const [expCode, setExpCode] = useState("");
    const [expText, setExpText] = useState("");
    const [projectCode, setProjectCode] = useState("");

    const [cariListLoading, cariListRecords, _, __, refetch] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.companyStore.getCompanyCariRecords.bind(null, {
            subCompanyId,
            limit: 10,
            page: pageParam,
            startDate:
                Array.isArray(selectedDateRange) &&
                selectedDateRange.length === 2 &&
                selectedDateRange[0] &&
                selectedDateRange[1]
                    ? moment(selectedDateRange[0]).format("YYYY-MM-DD")
                    : undefined,
            endDate:
                Array.isArray(selectedDateRange) &&
                selectedDateRange.length === 2 &&
                selectedDateRange[0] &&
                selectedDateRange[1]
                    ? moment(selectedDateRange[1]).format("YYYY-MM-DD")
                    : undefined,
            city: city === "all" || !city ? undefined : city,
            ...(selectedCompany && {companyId: selectedCompany}),
            cariCode: cariCode ? cariCode.toLocaleLowerCase("tr-TR") : undefined,
            expCode: expCode ? expCode.toLocaleLowerCase("tr-TR") : undefined,
            cariCount: cariCount ? parseInt(cariCount) : undefined,
            searchText: searchText ? searchText.toLocaleLowerCase("tr-TR") : undefined,
            projectCode: projectCode ? projectCode.toLocaleLowerCase("tr-TR") : undefined
        }),
        initialCall: false,
        showLoading: true,
        dependencies: [
            subCompanyId,
            pageParam,
            selectedDateRange,
            city,
            selectedCompany,
            cariCode,
            cariCount,
            expCode,
            searchText,
            projectCode
        ]
    });

    const getExcelTemplateData = async () => {
        setLoadingExcelData(true);
        try {
            const response = await stores.companyStore.getCompanyCariRecords({
                subCompanyId,
                limit: 10,
                page: pageParam,
                ...(selectedDateRange?.length === 2 && {
                    startDate: selectedDateRange?.[0]
                        ? moment(selectedDateRange[0]).format("YYYY-MM-DD")
                        : moment()
                              .subtract(30, "days")
                              .format("YYYY-MM-DD"),
                    endDate: selectedDateRange?.[1]
                        ? moment(selectedDateRange[1]).format("YYYY-MM-DD")
                        : moment().format("YYYY-MM-DD")
                }),
                city: city === "all" || !city ? undefined : city,
                ...(selectedCompany && {companyId: selectedCompany}),
                cariCode: cariCode ? cariCode.toLocaleLowerCase("tr-TR") : undefined,
                expCode: expCode ? expCode.toLocaleLowerCase("tr-TR") : undefined,
                cariCount: cariCount ? parseInt(cariCount) : undefined,
                searchText: searchText ? searchText.toLocaleLowerCase("tr-TR") : undefined,
                projectCode: projectCode ? projectCode.toLocaleLowerCase("tr-TR") : undefined,
                excel: true
            });

            const base64Data = response?.data?.data;
            const blobUrl = await b64DecodeToUrl(base64Data);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.setAttribute("download", "cari_kayitlar.xlsx");
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.log("error", error);
        } finally {
            setLoadingExcelData(false);
        }
    };

    useEffect(() => {
        refetch();
    }, []);

    const handleDateChange = value => {
        setPageParam(1);
        if (!value || value.length === 0) {
            setSelectedDateRange([]);
        } else {
            setSelectedDateRange(value);
        }
    };

    return (
        <div style={{width: "100%"}}>
            <span style={styles.header}>Cari Kayıtlar</span>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "30px",
                    flexWrap: "wrap",
                    margin: "30px 0"
                }}
            >
                <div style={styles.filterContainer}>
                    <span>
                        Tarih{" "}
                        <Tooltip title="Son 30 günün verisi gösterilmektedir.">
                            <InfoCircleOutlined />
                        </Tooltip>
                    </span>
                    <DatePicker.RangePicker
                        placeholder={["Başlangıç Tarihi", "Bitiş Tarihi"]}
                        onChange={handleDateChange}
                        value={selectedDateRange}
                        locale={locale}
                        allowClear
                    />
                </div>
                <div style={styles.filterContainer}>
                    <CityFilter
                        showAllOption
                        showLabel
                        label="Şehir"
                        value={city}
                        selectMarginBottom="0"
                        onChange={value => {
                            setCity(value);
                            setPageParam(1);
                        }}
                    />
                </div>
                <div style={styles.filterContainer}>
                    <CompanyFilter
                        showAllOption
                        allowClear
                        showSearch
                        selectMarginBottom="0"
                        value={selectedCompany}
                        onChange={value => {
                            setPageParam(1);
                            setSelectedCompany(value);
                        }}
                    />
                </div>
                <div style={styles.filterContainer}>
                    <h5>Cari Kodu</h5>
                    <SearchInput
                        placeholder="Cari Kodu"
                        value={cariText}
                        data-testid="cariCodeSearch"
                        onSearch={value => {
                            setPageParam(1);
                            setCariCode(value);
                        }}
                        onChange={event => {
                            setCariText(event.target.value);
                        }}
                        enterButton={
                            <CustomButton
                                data-testid="cariCodeSearch_searchButton"
                                icon={<SearchOutlined />}
                            />
                        }
                        suffix={
                            cariText ? (
                                <CloseIcon
                                    data-testid="cariCodeSearch_deleteButton"
                                    onClick={() => {
                                        setPageParam(1);
                                        setCariCode("");
                                        setCariText("");
                                    }}
                                />
                            ) : (
                                <span />
                            )
                        }
                    />
                </div>
                <div style={styles.filterContainer}>
                    <h5>Cari Sayısı</h5>
                    <SearchInput
                        placeholder="Cari Sayısı"
                        value={cariCountText}
                        data-testid="cariCountSearch"
                        onSearch={value => {
                            setPageParam(1);
                            setCariCount(value);
                        }}
                        onChange={event => {
                            setCariCountText(event.target.value);
                        }}
                        enterButton={
                            <CustomButton
                                data-testid="cariCountSearch_searchButton"
                                icon={<SearchOutlined />}
                            />
                        }
                        suffix={
                            cariCountText ? (
                                <CloseIcon
                                    data-testid="cariCountSearch_deleteButton"
                                    onClick={() => {
                                        setPageParam(1);
                                        setCariCount("");
                                        setCariCountText("");
                                    }}
                                />
                            ) : (
                                <span />
                            )
                        }
                    />
                </div>
                <div style={styles.filterContainer}>
                    <h5>PN Kodu</h5>
                    <SearchInput
                        placeholder="PN Kodu"
                        value={expText}
                        data-testid="expCodeSearch"
                        onSearch={value => {
                            setPageParam(1);
                            setExpCode(value);
                        }}
                        onChange={event => {
                            setExpText(event.target.value);
                        }}
                        enterButton={
                            <CustomButton
                                data-testid="expCodeSearch_searchButton"
                                icon={<SearchOutlined />}
                            />
                        }
                        suffix={
                            expText ? (
                                <CloseIcon
                                    data-testid="expCodeSearch_deleteButton"
                                    onClick={() => {
                                        setPageParam(1);
                                        setExpText("");
                                        setExpCode("");
                                    }}
                                />
                            ) : (
                                <span />
                            )
                        }
                    />
                </div>
                <div style={styles.filterContainer}>
                    <h5>Proje Kodu</h5>
                    <SearchInput
                        placeholder="Proje Kodu"
                        value={projectCode}
                        data-testid="projectCodeSearch"
                        onSearch={value => {
                            setPageParam(1);
                            setProjectCode(value);
                        }}
                        onChange={event => {
                            setProjectCode(event.target.value);
                        }}
                        enterButton={
                            <CustomButton
                                data-testid="projectCodeSearch_searchButton"
                                icon={<SearchOutlined />}
                            />
                        }
                        suffix={
                            expText ? (
                                <CloseIcon
                                    data-testid="projectCodeSearch_deleteButton"
                                    onClick={() => {
                                        setPageParam(1);
                                        setProjectCode("");
                                    }}
                                />
                            ) : (
                                <span />
                            )
                        }
                    />
                </div>
                <div style={{marginTop: "15px"}}>
                    <CustomButton
                        type="primary"
                        data-testid="exportWithExcelButton"
                        onClick={async () => {
                            setLoadingExcelData(true);
                            await getExcelTemplateData();
                        }}
                        loading={loadingExcelData}
                        icon={<DownloadOutlined />}
                    >
                        Excel Çıktısı İndir
                    </CustomButton>
                </div>
            </div>
            <Table
                rowKey={(_, index) => index.toString()}
                dataSource={cariListRecords?.data?.rows}
                loading={cariListLoading}
                scroll={{x: "max-content"}}
                pagination={{
                    total: cariListRecords?.data?.dataCount,
                    current: pageParam,
                    hideOnSinglePage: true,
                    showSizeChanger: false,
                    onChange: page => {
                        setPageParam(page);
                    }
                }}
                locale={{
                    emptyText: <EmptyMessage />
                }}
            >
                <Table.Column
                    title="Detay"
                    dataIndex="detail"
                    key="detail"
                    render={(detail, record) => (
                        <CustomButton
                            onClick={event => {
                                event.preventDefault();
                                window.open(
                                    "/panel/credit-logs/cari-list/" + record?.subCompanyId,
                                    "_blank"
                                );
                            }}
                            icon={<FileSearchOutlined />}
                        />
                    )}
                />
                <Table.Column
                    title="Alt Firma Id"
                    dataIndex="subCompanyId"
                    key="subCompanyId"
                    render={subcompanyId => (
                        <CustomButton
                            type="link"
                            href={"/panel/companies/sub-company/" + subcompanyId}
                            data-testid="subcompanyDetail"
                            onClick={event => {
                                event.preventDefault();
                                window.location.href =
                                    "/panel/companies/sub-company/" + subcompanyId;
                            }}
                        >
                            {subcompanyId}
                        </CustomButton>
                    )}
                />
                <Table.Column
                    title="Alt Firma Adı"
                    dataIndex="subCompanyName"
                    key="subCompanyName"
                    render={subCompanyName => (
                        <span>{subCompanyName === "" ? "-" : subCompanyName}</span>
                    )}
                />
                <Table.Column
                    title="Firma Adı"
                    dataIndex="companyName"
                    key="companyName"
                    render={companyName => <span>{companyName === "" ? "-" : companyName}</span>}
                />
                <Table.Column
                    title="VKN"
                    dataIndex="taxNo"
                    key="taxNo"
                    render={taxNo => <span>{taxNo === "" ? "-" : taxNo}</span>}
                />
                <Table.Column title="Cari Kodu" dataIndex="cariCode" key="cariCode" />
                <Table.Column title="Cari Sayısı" dataIndex="cariCount" key="cariCount" />
                <Table.Column
                    title="PN Kodu"
                    dataIndex="expCode"
                    key="expCode"
                    render={expCode => <span>{expCode === "" ? "-" : expCode}</span>}
                />
                <Table.Column
                    title="Proje Kodu"
                    dataIndex="projectCode"
                    key="projectCode"
                    render={projectCode => <span>{projectCode === "" ? "-" : projectCode}</span>}
                />
                <Table.Column
                    title="Toplam Alacak"
                    dataIndex="totalIn"
                    key="totalIn"
                    render={totalIn => (
                        <span>
                            {totalIn === "" ? "0 TL" : parseFloat(totalIn).toFixed(2) + " TL"}
                        </span>
                    )}
                />
                <Table.Column
                    title="Toplam Borç"
                    dataIndex="totalOut"
                    key="totalOut"
                    render={totalOut => (
                        <span>
                            {totalOut === "" ? "0 TL" : parseFloat(totalOut).toFixed(2) + " TL"}
                        </span>
                    )}
                />
                <Table.Column
                    title="Toplam"
                    dataIndex="total"
                    key="total"
                    render={total => (
                        <span>{total === "" ? "0 TL" : parseFloat(total).toFixed(2) + " TL"}</span>
                    )}
                />
            </Table>
        </div>
    );
};

const styles = {
    header: {
        fontSize: 20,
        fontWeight: "bold",
        width: "100%",
        paddingBottom: 16
    },
    filterContainer: {
        flexDirection: "column",
        display: "flex",
        flex: 1
    }
};
